import axios from "axios";

const axiosInstance = axios.create({
  baseURL: process.env.REACT_APP_API_BASE_URL || "http://localhost:5000/api",
  headers: {
    "Content-Type": "application/json",
  },
});

// Request interceptor to add auth token. Superadmin endpoints use a
// separate `sa_token` so a stray tenant token never leaks into the
// site-owner console and vice versa.
const isSuperAdminUrl = (url = "") => {
  const path = url.startsWith("http") ? new URL(url).pathname : url;
  return path.includes("/superadmin");
};

axiosInstance.interceptors.request.use(
  (config) => {
    const useSa = isSuperAdminUrl(config.url || "");
    const token = useSa
      ? localStorage.getItem("sa_token")
      : localStorage.getItem("token");
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Response interceptor
axiosInstance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    // Handle 401 Unauthorized errors — route to the right login screen
    // depending on which surface the failing request came from.
    if (error.response?.status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;
      const path = (window.location.pathname || "").toLowerCase();
      const reqUrl = originalRequest.url || "";
      // Never bounce during the login flow itself — the login POST's own
      // 401 (bad credentials) must reach the form, and background 401s
      // from other contexts fired while the login page is mounted must
      // not wipe the token we're about to store.
      const onLoginScreen =
        path.includes("/login") || path.includes("/super-admin-login");
      // AuthContext handles /auth/verify failures on its own.
      const isVerify = reqUrl.includes("/auth/verify");
      if (!onLoginScreen && !isVerify) {
        if (isSuperAdminUrl(reqUrl)) {
          localStorage.removeItem("sa_token");
          localStorage.removeItem("superAdmin");
          window.location.href = "/super-admin-login";
        } else {
          localStorage.removeItem("token");
          localStorage.removeItem("user");
          window.location.href = "/login";
        }
      }
      return Promise.reject(error);
    }

    // Temporary password still active: the backend refuses everything
    // except the password change, so send the user to that screen.
    if (
      error.response?.status === 403 &&
      error.response?.data?.code === "PASSWORD_CHANGE_REQUIRED" &&
      !(window.location.pathname || "").startsWith("/change-password")
    ) {
      window.location.href = "/change-password";
      return Promise.reject(error.response.data);
    }

    // Platform maintenance: tenant screens move to the status page. The
    // SuperAdmin console is exempt server-side and never gets this code.
    if (
      error.response?.status === 503 &&
      error.response?.data?.code === "MAINTENANCE" &&
      !(window.location.pathname || "").startsWith("/maintenance")
    ) {
      window.location.href = "/maintenance";
      return Promise.reject(error.response.data);
    }

    // Handle network errors
    if (!error.response) {
      console.error("Network Error:", error.message);
      return Promise.reject({
        message: "Network error. Please check your connection and try again.",
      });
    }

    // Handle other errors
    return Promise.reject(
      error.response?.data || {
        message: "An error occurred. Please try again later.",
      }
    );
  }
);

export default axiosInstance;
